import { Component, computed, effect, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FieldTree } from '@angular/forms/signals';
import { of } from 'rxjs';
import { BS7671DeratingRequestDto, DeratedCurrentCalculationDto, ERA6930P3DeratingRequestDto, ERA6930P5DeratingRequestDto, IEC60502P2DeratingRequestDto, MceApiService, SustainedCurrentDto, UDRDeratingRequestDto } from '../../../../core/mceapi.client.generated';
import { apiOpt, appOpt } from '../../../../core/mceapi.client.helpers';
import { CalculationBoxComponent } from '../../../../shared/components/calculation-box/calculation-box.component';
import { BS7671_STANDARD_GUID, ERA6930P3_STANDARD_GUID, ERA6930P5_STANDARD_GUID, IEC60502P2_STANDARD_GUID, UDR_STANDARD_GUID } from '../../../../shared/constants';
import { isSome, match } from '../../../../shared/functions/functional';
import { createLoadDto, Load } from '../../load/load.model';
import { createProtectionDto, Protection } from '../../protection/cable-protection.component';
import { createCableSelectionDto, isSelectedCableValid, SelectedCable } from '../../selection/cable-selection.model';
import { CableSettings } from '../../settings/cable-settings';
import { createSystemVoltageDto, SystemVoltage } from '../../system-voltage/system-voltage.model';
import { createAmbientTemperatureDto } from '../ambient-temperature/ambient-temperature.model';
import { createBuriedDto } from '../buried/buried.model';
import { getSecondaryMethodGuid, Installation, isValidInstallation } from '../cable-installation.model';
import { createFactorsBS7671Dto } from '../factors-bs7671/factors-bs7671.model';
import { createFactorsIEC60502P2Dto } from '../factors-iec60502p2/factors-iec60502p2.model';
import { createGroupingDto } from '../grouping/grouping.model';
import { createSingleCoreArrangementDto } from '../single-core-arrangement/single-core-arrangement.model';
import { createUserDefinedFactorDto } from '../user-defined-factor/user-defined-factor.model';
import { CableCalculationsStore } from '../../cable-calculations.store';

type CompleteCurrent = SustainedCurrentDto & {
  i: { i: number };
};

type CompleteDeratedCurrentCalculation = DeratedCurrentCalculationDto & {
  standard: { reference: string };
  table: { reference: string };
  deratingFactors: NonNullable<DeratedCurrentCalculationDto['deratingFactors']>;
  base: { i: number };
  actual: CompleteCurrent;
  conductorTemperature: { conductorTemperature: number };
};

function requireCompleteCalculation(calculation: DeratedCurrentCalculationDto): CompleteDeratedCurrentCalculation {
  if (calculation.standard?.reference === undefined
    || calculation.table?.reference === undefined
    || calculation.deratingFactors === undefined
    || calculation.base?.i === undefined
    || calculation.actual?.i?.i === undefined
    || calculation.conductorTemperature?.conductorTemperature === undefined) {
    throw new Error('Capacity calculation DTO is missing required values.');
  }

  return calculation as CompleteDeratedCurrentCalculation;
}

function formatSuggestedCable(suggestedCable: SustainedCurrentDto): string {
  if (suggestedCable.csa?.csa === undefined || suggestedCable.i?.i === undefined) {
    throw new Error('Suggested cable DTO is missing required values.');
  }

  return `Suggested Cable: ${suggestedCable.csa.csa} mm² [${Math.round(suggestedCable.i.i)} A]`;
}

@Component({
  selector: 'app-capacity-calculation',
  imports: [CalculationBoxComponent],
  templateUrl: './capacity-calculation.component.html',
  styles: ``,
})
export class CapacityCalculationComponent {

  readonly installation = input.required<FieldTree<Installation>>();
  readonly systemVoltage = input.required<FieldTree<SystemVoltage>>();
  readonly load = input.required<FieldTree<Load>>();
  readonly protection = input.required<FieldTree<Protection>>();
  readonly selectedCable = input.required<FieldTree<SelectedCable>>();
  readonly settings = input.required<FieldTree<CableSettings>>();

  private mceApiService = inject(MceApiService);
  readonly store = inject(CableCalculationsStore);

  selectedCableValid = computed(() => isSelectedCableValid(this.selectedCable())());

  udrDeratingRequest = computed<UDRDeratingRequestDto>(() => ({
    selectedCable: apiOpt(createCableSelectionDto(this.selectedCable())()),
    csa: this.selectedCable().csa().value(),
    load: createLoadDto(this.load())(),
    protectiveDevice: apiOpt(createProtectionDto(this.protection())()),
    sustainedRating: this.installation().udrFactors.sustainedRating().value(),
    referenceTemperature: this.installation().udrFactors.referenceTemperature().value(),
    simpleDesignCurrent: this.settings().simpleDesignCurrent().value(),
    }));


  bs7671DeratingRequest = computed<BS7671DeratingRequestDto>(() => ({
    selectedCable: apiOpt(createCableSelectionDto(this.selectedCable())()),
    singleCoreArrangement: apiOpt(createSingleCoreArrangementDto(this.installation().singleCoreArrangement)()),
    systemVoltage: createSystemVoltageDto(this.systemVoltage())(),
    load: createLoadDto(this.load())(),
    protectiveDevice: apiOpt(createProtectionDto(this.protection())()),
    secondaryMethod: this.installation().bs7671Factors.secondaryMethodGuid().value(),
    ambientTemperature: createAmbientTemperatureDto(this.installation().bs7671Factors.ambientTemperature)(),
    buried: createBuriedDto(this.installation().bs7671Factors.buried)(),
    grouping: createGroupingDto(this.installation().bs7671Factors.grouping)(),
    thermalInsulation: createFactorsBS7671Dto(this.installation().bs7671Factors)().thermalInsulationThickness,
    userDefinedFactor: apiOpt(createUserDefinedFactorDto(this.installation().bs7671Factors.userDefinedFactor)()),
    simpleDesignCurrent: this.settings().simpleDesignCurrent().value(),
    }));


  era6930P3DeratingRequest = computed<ERA6930P3DeratingRequestDto>(() => ({
    selectedCable: apiOpt(createCableSelectionDto(this.selectedCable())()),
    singleCoreArrangement: apiOpt(createSingleCoreArrangementDto(this.installation().singleCoreArrangement)()),
    systemVoltage: createSystemVoltageDto(this.systemVoltage())(),
    load: createLoadDto(this.load())(),
    protectiveDevice: apiOpt(createProtectionDto(this.protection())()),
    secondaryMethod: this.installation().era6930P3Factors.secondaryMethodGuid().value(),
    ambientTemperature: createAmbientTemperatureDto(this.installation().era6930P3Factors.ambientTemperature)(),
    buried: createBuriedDto(this.installation().era6930P3Factors.buried)(),
    grouping: createGroupingDto(this.installation().era6930P3Factors.grouping)(),
    userDefinedFactor: apiOpt(createUserDefinedFactorDto(this.installation().era6930P3Factors.userDefinedFactor)()),
    simpleDesignCurrent: this.settings().simpleDesignCurrent().value(),
  }));

  era6930P5DeratingRequest = computed<ERA6930P5DeratingRequestDto>(() => ({
    selectedCable: apiOpt(createCableSelectionDto(this.selectedCable())()),
    singleCoreArrangement: apiOpt(createSingleCoreArrangementDto(this.installation().singleCoreArrangement)()),
    systemVoltage: createSystemVoltageDto(this.systemVoltage())(),
    load: createLoadDto(this.load())(),
    protectiveDevice: apiOpt(createProtectionDto(this.protection())()),
    secondaryMethod: this.installation().era6930P5Factors.secondaryMethodGuid().value(),
    ambientTemperature: createAmbientTemperatureDto(this.installation().era6930P5Factors.ambientTemperature)(),
    buried: createBuriedDto(this.installation().era6930P5Factors.buried)(),
    grouping: createGroupingDto(this.installation().era6930P5Factors.grouping)(),
    userDefinedFactor: apiOpt(createUserDefinedFactorDto(this.installation().era6930P5Factors.userDefinedFactor)()),
    simpleDesignCurrent: this.settings().simpleDesignCurrent().value(),
  }));

  iec60502DeratingRequest = computed<IEC60502P2DeratingRequestDto>(() => ({
    selectedCable: apiOpt(createCableSelectionDto(this.selectedCable())()),
    singleCoreArrangement: apiOpt(createSingleCoreArrangementDto(this.installation().singleCoreArrangement)()),
    systemVoltage: createSystemVoltageDto(this.systemVoltage())(),
    load: createLoadDto(this.load())(),
    protectiveDevice: apiOpt(createProtectionDto(this.protection())()),
    secondaryMethod: this.installation().iec60502Factors.secondaryMethodGuid().value(),
    ambientTemperature: createAmbientTemperatureDto(this.installation().iec60502Factors.ambientTemperature)(),
    buried: createBuriedDto(this.installation().iec60502Factors.buried)(),
    grouping: createGroupingDto(this.installation().iec60502Factors.grouping)(),
    numberOfTrays: createFactorsIEC60502P2Dto(this.installation().iec60502Factors)().numberOfTrays,
    userDefinedFactor: apiOpt(createUserDefinedFactorDto(this.installation().iec60502Factors.userDefinedFactor)()),
    simpleDesignCurrent: this.settings().simpleDesignCurrent().value(),
  }));


  dependanciesValid = computed(() => {
    const standardGuid = this.installation().standardGuid().value();
    return isValidInstallation(this.installation())
      && this.systemVoltage()().valid() 
      && this.load()().valid()
      && this.selectedCable()().value().libraryGuid !== ''
      && !(standardGuid !== UDR_STANDARD_GUID && getSecondaryMethodGuid(this.installation())() === '')
      && !(standardGuid === '' || standardGuid === undefined)
  });

  calculationRx = rxResource({
    params: () => ({
      standardGuid: this.installation().standardGuid().value(),
      udr: this.udrDeratingRequest(),
      bs7671: this.bs7671DeratingRequest(),
      era6930p3: this.era6930P3DeratingRequest(),
      era6930p5: this.era6930P5DeratingRequest(),
      iec60502: this.iec60502DeratingRequest(),
    }),
    stream: ({ params }) => {
      const path = this.installation();
      //if (params.standardGuid !== UDR_STANDARD_GUID &&  getSecondaryMethodGuid(path)() === '') return of(undefined);
      //if (params.standardGuid === '' || params.standardGuid === undefined) return of(undefined);
      if (!this.dependanciesValid()) return of(undefined);
      switch (params.standardGuid) {
        case UDR_STANDARD_GUID:
          return path.udrFactors().valid()
            ? this.mceApiService.v1CableCapacityCalculationUdr(params.udr)
            : of(undefined);
        case BS7671_STANDARD_GUID:
          return path.bs7671Factors().valid()
            ? this.mceApiService.v1CableCapacityCalculationBs7671(params.bs7671)
            : of(undefined);
        case ERA6930P3_STANDARD_GUID:
          return path.era6930P3Factors().valid()
            ? this.mceApiService.v1CableCapacityCalculationEra6930p3(params.era6930p3)
            : of(undefined);
        case ERA6930P5_STANDARD_GUID:
          return path.era6930P5Factors().valid()
            ? this.mceApiService.v1CableCapacityCalculationEra6930p5(params.era6930p5)
            : of(undefined);
        case IEC60502P2_STANDARD_GUID:
          return path.iec60502Factors().valid()
            ? this.mceApiService.v1CableCapacityCalculationIec60502p2(params.iec60502)
            : of(undefined);
        default: return of(undefined);
      }
    }
  });
  calculation = computed<CompleteDeratedCurrentCalculation | undefined>(() => {
    const calculation = this.calculationRx.value();
    return calculation === undefined ? undefined : requireCompleteCalculation(calculation);
  });
  _store = effect(() => { this.store.setCapacity(this.calculation()); });
  calculationStatus = computed(() =>
    this.calculationRx.value()?.status
      ?? {
        case: 'Info',
        message:
          !this.selectedCableValid()
            ? 'Please select a cable to see installation details.'
            : 'Please ensure a valid cable installation is specified.'
      }
  );



  display = computed(() => {
    const calc = this.calculation();
    return ({

      table: calc ? `${calc.standard.reference}: ${calc.table.reference}` : '',

      baseCurrent: calc ? `Base current: ${calc.base.i} A` : '',
      sustainedCurrent: calc ? `Sustained current: ${Math.round(calc.actual.i.i)} A` : '',
      suggestedCable:
        calc 
          ? match(
            () => `No cable found that meets the requirements`,
            formatSuggestedCable
          )(appOpt(calc.suggested ?? {}))
          : '',
      suggestedCableShow: calc ? isSome(appOpt(calc.suggested ?? {})) : false,

      conductorTemperature: calc ? `Conductor temperature: ${Math.round(calc.conductorTemperature.conductorTemperature)} °C` : '',

      deratingFactors: calc ? calc.deratingFactors : [],
      deratingFactorsShow: calc ? calc.deratingFactors.length > 0 : false,

      showUDRNote: this.installation().standardGuid().value() === UDR_STANDARD_GUID && calc !== undefined,

    });

  });



}
